import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Divider,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { GetProductsParams, Category, Brand } from "../../types";
import { productService } from "../../services/productService";

interface ProductAdvancedSearchProps {
    open: boolean;
    onClose: () => void;
    onSearch: (params: GetProductsParams) => void;
    initialParams: GetProductsParams;
}

// Shared shape for the three tri-state Yes/No/All filters below (Active,
// Visible, Featured) — keeps the Select value/onChange mapping identical
// and in one place instead of three near-duplicate inline blocks.
type TriState = "" | "yes" | "no";
const toTriState = (value: boolean | undefined): TriState =>
    value === undefined ? "" : value ? "yes" : "no";
const fromTriState = (value: TriState): boolean | undefined =>
    value === "" ? undefined : value === "yes";

const DEFAULT_PARAMS: GetProductsParams = { skip: 0, limit: 50, sort_by: "featured" };

export const ProductAdvancedSearch: React.FC<ProductAdvancedSearchProps> = ({
    open,
    onClose,
    onSearch,
    initialParams,
}) => {
    const [searchParams, setSearchParams] =
        useState<GetProductsParams>(initialParams);
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);

    // Load category/brand lists once when the dialog opens — used to
    // populate the two Autocomplete pickers below.
    useEffect(() => {
        if (!open) return;
        productService
            .getCategories({ active_only: false, limit: 500 })
            .then(setCategories)
            .catch(() => setCategories([]));
        productService
            .getBrands({ active_only: false, limit: 500 })
            .then(setBrands)
            .catch(() => setBrands([]));
    }, [open]);

    useEffect(() => {
        if (open) setSearchParams(initialParams);
    }, [initialParams, open]);

    const handleChange = (field: keyof GetProductsParams, value: any) => {
        setSearchParams((prev) => ({
            ...prev,
            [field]: value === "" ? undefined : value,
        }));
    };

    const handleSearch = () => {
        onSearch({ ...searchParams, skip: 0 });
        onClose();
    };

    const handleClear = () => {
        const cleared: GetProductsParams = {
            ...DEFAULT_PARAMS,
            limit: searchParams.limit || 50,
        };
        setSearchParams(cleared);
        onSearch(cleared);
        onClose();
    };

    const selectedCategory =
        categories.find((c) => c.category_id === searchParams.category_id) ||
        null;
    const selectedBrand =
        brands.find((b) => b.brand_id === searchParams.brand_id) || null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Advanced Product Search</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Status & Visibility */}
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Status &amp; Visibility
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Active</InputLabel>
                                <Select<TriState>
                                    value={toTriState(searchParams.is_active)}
                                    label="Active"
                                    onChange={(e) =>
                                        handleChange(
                                            "is_active",
                                            fromTriState(e.target.value as TriState),
                                        )
                                    }
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="yes">Active only</MenuItem>
                                    <MenuItem value="no">Inactive only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Visibility</InputLabel>
                                <Select<TriState>
                                    value={toTriState(searchParams.is_visible)}
                                    label="Visibility"
                                    onChange={(e) =>
                                        handleChange(
                                            "is_visible",
                                            fromTriState(e.target.value as TriState),
                                        )
                                    }
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="yes">Visible only</MenuItem>
                                    <MenuItem value="no">Hidden only</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Featured</InputLabel>
                                <Select<TriState>
                                    value={toTriState(searchParams.is_featured)}
                                    label="Featured"
                                    onChange={(e) =>
                                        handleChange(
                                            "is_featured",
                                            fromTriState(e.target.value as TriState),
                                        )
                                    }
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="yes">Featured only</MenuItem>
                                    <MenuItem value="no">Not featured</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Category & Brand */}
                    <Typography variant="subtitle2" gutterBottom>
                        Category &amp; Brand
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                size="small"
                                options={categories}
                                value={selectedCategory}
                                getOptionLabel={(c) => c.name}
                                isOptionEqualToValue={(a, b) =>
                                    a.category_id === b.category_id
                                }
                                onChange={(_, newValue) =>
                                    handleChange(
                                        "category_id",
                                        newValue?.category_id,
                                    )
                                }
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        key={option.category_id}
                                        sx={{
                                            pl: `${16 + (option.level || 0) * 16}px !important`,
                                        }}
                                    >
                                        {option.name}
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} label="Category" />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                size="small"
                                options={brands}
                                value={selectedBrand}
                                getOptionLabel={(b) => b.name}
                                isOptionEqualToValue={(a, b) =>
                                    a.brand_id === b.brand_id
                                }
                                onChange={(_, newValue) =>
                                    handleChange("brand_id", newValue?.brand_id)
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label="Brand" />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Price & Stock */}
                    <Typography variant="subtitle2" gutterBottom>
                        Price &amp; Stock
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Min Selling Price"
                                type="number"
                                value={searchParams.min_price ?? ""}
                                onChange={(e) =>
                                    handleChange(
                                        "min_price",
                                        e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined,
                                    )
                                }
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Max Selling Price"
                                type="number"
                                value={searchParams.max_price ?? ""}
                                onChange={(e) =>
                                    handleChange(
                                        "max_price",
                                        e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined,
                                    )
                                }
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Min Cost Price"
                                type="number"
                                value={searchParams.min_cost_price ?? ""}
                                onChange={(e) =>
                                    handleChange(
                                        "min_cost_price",
                                        e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined,
                                    )
                                }
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Max Cost Price"
                                type="number"
                                value={searchParams.max_cost_price ?? ""}
                                onChange={(e) =>
                                    handleChange(
                                        "max_cost_price",
                                        e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined,
                                    )
                                }
                                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!searchParams.on_sale_only}
                                        onChange={(e) =>
                                            handleChange(
                                                "on_sale_only",
                                                e.target.checked
                                                    ? true
                                                    : undefined,
                                            )
                                        }
                                    />
                                }
                                label="On Sale Only"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stock Status</InputLabel>
                                <Select
                                    value={searchParams.stock_status || ""}
                                    label="Stock Status"
                                    onChange={(e) =>
                                        handleChange(
                                            "stock_status",
                                            e.target.value,
                                        )
                                    }
                                >
                                    <MenuItem value="">Any</MenuItem>
                                    <MenuItem value="in_stock">In Stock</MenuItem>
                                    <MenuItem value="low_stock">Low Stock</MenuItem>
                                    <MenuItem value="out_of_stock">
                                        Out of Stock
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Date Added */}
                    <Typography variant="subtitle2" gutterBottom>
                        Date Added
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                type="date"
                                value={searchParams.created_from || ""}
                                onChange={(e) =>
                                    handleChange("created_from", e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To Date"
                                type="date"
                                value={searchParams.created_to || ""}
                                onChange={(e) =>
                                    handleChange("created_to", e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Sorting */}
                    <Typography variant="subtitle2" gutterBottom>
                        Sort Results
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={searchParams.sort_by || "featured"}
                                    label="Sort By"
                                    onChange={(e) =>
                                        handleChange("sort_by", e.target.value)
                                    }
                                >
                                    <MenuItem value="featured">Featured</MenuItem>
                                    <MenuItem value="price-asc">
                                        Price: Low to High
                                    </MenuItem>
                                    <MenuItem value="price-desc">
                                        Price: High to Low
                                    </MenuItem>
                                    <MenuItem value="name-asc">
                                        Name: A to Z
                                    </MenuItem>
                                    <MenuItem value="name-desc">
                                        Name: Z to A
                                    </MenuItem>
                                    <MenuItem value="newest">
                                        Newest First
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleClear}
                    startIcon={<ClearIcon />}
                    color="warning"
                >
                    Clear All
                </Button>
                <Button
                    onClick={handleSearch}
                    variant="contained"
                    startIcon={<SearchIcon />}
                >
                    Search
                </Button>
            </DialogActions>
        </Dialog>
    );
};
